import { useState, useEffect } from "react"
import {
  ShieldIcon,
  QrCodeIcon,
  BellIcon,
  MapPinIcon,
  UsersIcon,
  CarIcon,
  BikeIcon,
  GraduationCapIcon,
  HeartIcon,
  LeafIcon,
  MenuIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet"

// ─── Types ───────────────────────────────────────────────────────────────────

type KitStatus = "ready" | "coming_soon"
type KitFamily = "Vehicle SafetyQR" | "Personal SafetyQR" | "Site SafetyQR"

interface KitDescriptor {
  id: string
  family: KitFamily
  name: string
  stickerCount: number
  placement: string
  status: KitStatus
  icon: LucideIcon
  tagline: string
}

interface KitRequestFormValues {
  requesterName: string
  phone: string
  kitName: string
  area: string
  quantity: string
  note: string
}

interface NotifyMeFormValues {
  requesterName: string
  contactValue: string
  kitName: string
}

type ActiveSheet =
  | { kind: "request"; kit: KitDescriptor }
  | { kind: "notify_me"; kit: KitDescriptor }
  | null

// ─── Static data ─────────────────────────────────────────────────────────────

const KITS: KitDescriptor[] = [
  {
    id: "car_safety_kit",
    family: "Vehicle SafetyQR",
    name: "Car Safety Kit",
    stickerCount: 2,
    placement: "Windshield + dashboard",
    status: "ready",
    icon: CarIcon,
    tagline: "Scan-to-alert for vehicle incidents and emergencies.",
  },
  {
    id: "riders_safety_kit",
    family: "Vehicle SafetyQR",
    name: "Riders Safety Kit",
    stickerCount: 2,
    placement: "Helmet + phone case",
    status: "ready",
    icon: BikeIcon,
    tagline: "QR on your helmet and phone case — help finds you faster.",
  },
  {
    id: "students_safety_qr",
    family: "Personal SafetyQR",
    name: "Students SafetyQR",
    stickerCount: 2,
    placement: "School bag + notebook / water bottle / ID card",
    status: "ready",
    icon: GraduationCapIcon,
    tagline: "One scan connects school, parent, and emergency contact.",
  },
  {
    id: "women_safety_qr",
    family: "Personal SafetyQR",
    name: "Women SafetyQR",
    stickerCount: 2,
    placement: "Phone case / lanyard + handbag / wallet / keychain",
    status: "ready",
    icon: ShieldIcon,
    tagline: "Discreet stickers. Instant alert when it matters most.",
  },
  {
    id: "elderly_safety_qr",
    family: "Personal SafetyQR",
    name: "Elderly SafetyQR",
    stickerCount: 2,
    placement: "Walking aid / phone / ID pouch + wallet / medicine pouch",
    status: "ready",
    icon: HeartIcon,
    tagline: "Aid workers and responders get key details instantly.",
  },
  {
    id: "plantations_safety_qr",
    family: "Site SafetyQR",
    name: "SafetyQR for Plantations",
    stickerCount: 5,
    placement: "Five zone tags across the plantation site",
    status: "ready",
    icon: LeafIcon,
    tagline: "Five zone stickers cover your entire plantation site.",
  },
]

const FAMILY_ORDER: KitFamily[] = [
  "Vehicle SafetyQR",
  "Personal SafetyQR",
  "Site SafetyQR",
]

const BENEFITS = [
  {
    icon: QrCodeIcon,
    title: "No app needed",
    description: "Anyone can scan — no install, no account required.",
  },
  {
    icon: BellIcon,
    title: "Instant alert",
    description: "Emergency contacts are notified the moment a sticker is scanned.",
  },
  {
    icon: MapPinIcon,
    title: "Works across India",
    description: "From city roads to plantation zones, every situation is covered.",
  },
  {
    icon: ShieldIcon,
    title: "Privacy-first",
    description: "Your data is never shown publicly. Only responders and your emergency contacts see it.",
  },
  {
    icon: UsersIcon,
    title: "Every life situation",
    description: "Students, women, elderly, vehicles, and plantation sites — all in one platform.",
  },
]

const HOW_IT_WORKS_STEPS = [
  {
    step: 1,
    title: "Request your kit",
    description: "Choose the right SafetyQR kit and we'll deliver it to you.",
  },
  {
    step: 2,
    title: "Place your stickers",
    description: "Stick them on your bag, helmet, vehicle, or site zone — wherever makes sense.",
  },
  {
    step: 3,
    title: "Anyone scans in an emergency",
    description: "No app needed. A simple QR scan opens a report form and sends an alert.",
  },
  {
    step: 4,
    title: "Contacts notified instantly",
    description: "Your emergency contact receives the details and can act immediately.",
  },
]

const PUBLIC_ROUTE_LINKS = {
  activate: "/register",
  report: "/report",
  myDevice: "/my-device",
  fleetSafetySuperAdmin: "/fleet-safety/super-admin",
  support: "/grievance",
  privacy: "/privacy",
  terms: "/terms",
} as const

// ─── Shared constants ─────────────────────────────────────────────────────────

const INPUT_CLASS =
  "mt-1 w-full rounded-2xl border border-border bg-background/90 px-4 py-3 text-sm shadow-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15"
const LABEL_CLASS = "text-sm font-medium text-foreground"
const HELP_CLASS = "mt-1 text-xs leading-5 text-muted-foreground"

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isValidIndianPhone(phone: string): boolean {
  const cleaned = phone.replace(/\s/g, "").replace(/^(\+91|91)/, "")
  return /^[6-9]\d{9}$/.test(cleaned)
}

// ─── LandingPage (root) ───────────────────────────────────────────────────────

export function LandingPage() {
  const [activeSheet, setActiveSheet] = useState<ActiveSheet>(null)

  const defaultRequestKit = KITS.find((k) => k.status === "ready") ?? KITS[2]

  return (
    <div className="min-h-svh">
      <LandingNav onRequestKit={() => setActiveSheet({ kind: "request", kit: defaultRequestKit })} />

      <main>
        <HeroSection
          onRequestKit={() => setActiveSheet({ kind: "request", kit: defaultRequestKit })}
        />
        <KitsSection
          onRequestKit={(kit) => setActiveSheet({ kind: "request", kit })}
          onNotifyMe={(kit) => setActiveSheet({ kind: "notify_me", kit })}
        />
        <HowItWorksSection />
        <WhySafetyQRSection />
      </main>

      <LandingFooter />

      <KitRequestSheet
        open={activeSheet?.kind === "request"}
        kit={activeSheet?.kind === "request" ? activeSheet.kit : null}
        onClose={() => setActiveSheet(null)}
      />
      <NotifyMeSheet
        open={activeSheet?.kind === "notify_me"}
        kit={activeSheet?.kind === "notify_me" ? activeSheet.kit : null}
        onClose={() => setActiveSheet(null)}
      />
    </div>
  )
}

// ─── LandingNav ───────────────────────────────────────────────────────────────

function LandingNav({ onRequestKit }: { onRequestKit: () => void }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const navLinks = [
    { label: "Kits", href: "#kits" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "Activate", href: PUBLIC_ROUTE_LINKS.activate },
    { label: "Report an Incident", href: PUBLIC_ROUTE_LINKS.report },
  ]

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-200 ${
        scrolled
          ? "shell-panel border-b border-border/50"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2.5 shrink-0">
          <img
            src="/feedbacknfc-logo.svg"
            alt="FeedbackNFC"
            className="h-7 w-auto"
          />
          <span className="font-heading font-semibold text-lg">SafetyQR</span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <Button size="lg" variant="outline" asChild className="hidden lg:inline-flex">
            <a href={PUBLIC_ROUTE_LINKS.fleetSafetySuperAdmin}>Fleet Safety Super Admin</a>
          </Button>
          <Button size="lg" onClick={onRequestKit} className="hidden sm:inline-flex">
            Request a Kit
          </Button>
          {/* Mobile hamburger */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon className="size-5" />
          </Button>
        </div>
      </div>

      {/* Mobile menu sheet */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="flex flex-col pt-16">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation menu</SheetTitle>
            <SheetDescription>SafetyQR navigation links</SheetDescription>
          </SheetHeader>
          <nav className="flex flex-col gap-1 px-2">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="px-4 py-3 rounded-2xl text-sm font-medium hover:bg-muted transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="mt-auto px-2 pb-4">
            <Button className="w-full mb-2" size="lg" variant="outline" asChild>
              <a href={PUBLIC_ROUTE_LINKS.fleetSafetySuperAdmin}>Fleet Safety Super Admin</a>
            </Button>
            <Button
              className="w-full"
              size="lg"
              onClick={() => {
                setMobileMenuOpen(false)
                onRequestKit()
              }}
            >
              Request a Kit
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  )
}

// ─── HeroSection ─────────────────────────────────────────────────────────────

function HeroSection({ onRequestKit }: { onRequestKit: () => void }) {
  const readyKits = KITS.filter((k) => k.status === "ready")

  return (
    <section className="relative min-h-[90svh] flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 pt-24 pb-20 overflow-hidden">
      <div className="mx-auto max-w-4xl text-center space-y-8">
        {/* Kicker */}
        <div className="flex justify-center">
          <span className="shell-panel rounded-4xl border border-border/60 px-4 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-primary">
            Built for India
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.08] tracking-tight text-balance">
          Your Safety.
          <br />
          <span className="text-primary">One Scan Away.</span>
        </h1>

        {/* Subheadline */}
        <p className="mx-auto max-w-2xl text-lg sm:text-xl text-muted-foreground leading-8">
          QR sticker kits for vehicles, students, women, elderly, and plantation
          sites — designed for Indian conditions. No app. No hassle. Just scan.
        </p>

        {/* CTAs */}
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Button size="lg" onClick={onRequestKit} className="gap-2 text-base px-6 h-12">
            Request a Safety Kit
            <ArrowRightIcon className="size-4" />
          </Button>
          <Button size="lg" variant="outline" asChild className="text-base px-6 h-12">
            <a href="#kits">See All Kits</a>
          </Button>
        </div>

        {/* Kit family pill cluster */}
        <div className="flex flex-wrap items-center justify-center gap-2.5 pt-4">
          {readyKits.map((kit) => {
            const Icon = kit.icon
            return (
              <span
                key={kit.id}
                className="shell-panel rounded-4xl border border-border/60 px-4 py-2 text-sm font-medium flex items-center gap-2 text-foreground/80"
              >
                <Icon className="size-4 text-primary shrink-0" />
                {kit.name}
              </span>
            )
          })}
        </div>
      </div>

      {/* Decorative gradient blob */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full opacity-30"
        style={{
          background:
            "radial-gradient(ellipse at center, oklch(0.82 0.09 190.5 / 0.5), transparent 70%)",
          filter: "blur(48px)",
        }}
      />
    </section>
  )
}

// ─── KitsSection ─────────────────────────────────────────────────────────────

function KitsSection({
  onRequestKit,
  onNotifyMe,
}: {
  onRequestKit: (kit: KitDescriptor) => void
  onNotifyMe: (kit: KitDescriptor) => void
}) {
  return (
    <section id="kits" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Section header */}
        <div className="mb-12 space-y-3">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-primary shell-kicker">
            Safety Kits
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight">
            Find the right kit for you
          </h2>
          <p className="max-w-xl text-base leading-7 text-muted-foreground">
            Every kit is a set of QR stickers tailored to a specific life situation. Place them once, and they're ready whenever you need them.
          </p>
        </div>

        {/* Kit families */}
        {FAMILY_ORDER.map((family) => {
          const kits = KITS.filter((k) => k.family === family)
          return (
            <div key={family} className="mb-14">
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-5 shell-kicker">
                {family}
              </p>
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {kits.map((kit) => (
                  <KitCard
                    key={kit.id}
                    kit={kit}
                    onRequestKit={onRequestKit}
                    onNotifyMe={onNotifyMe}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ─── KitCard ─────────────────────────────────────────────────────────────────

function KitCard({
  kit,
  onRequestKit,
  onNotifyMe,
}: {
  kit: KitDescriptor
  onRequestKit: (kit: KitDescriptor) => void
  onNotifyMe: (kit: KitDescriptor) => void
}) {
  const Icon = kit.icon

  return (
    <Card className="shell-panel rounded-4xl border-border/70 flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="rounded-2xl bg-primary/10 p-2.5">
            <Icon className="size-5 text-primary" />
          </div>
          {kit.status === "ready" ? (
            <Badge variant="default" className="shrink-0">
              Launch Ready
            </Badge>
          ) : (
            <Badge variant="secondary" className="shrink-0">
              Coming Soon
            </Badge>
          )}
        </div>
        <CardTitle className="font-heading text-xl mt-3">{kit.name}</CardTitle>
        <CardDescription className="text-sm leading-6">{kit.tagline}</CardDescription>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-3 pb-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground">
            {kit.stickerCount} sticker{kit.stickerCount > 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <MapPinIcon className="size-4 shrink-0 mt-0.5 text-muted-foreground/70" />
          <span>{kit.placement}</span>
        </div>
      </CardContent>

      <CardFooter className="pt-5">
        {kit.status === "ready" ? (
          <Button className="w-full" onClick={() => onRequestKit(kit)}>
            Request this Kit
          </Button>
        ) : (
          <Button className="w-full" variant="outline" onClick={() => onNotifyMe(kit)}>
            Notify Me at Launch
          </Button>
        )}
      </CardFooter>
    </Card>
  )
}

// ─── HowItWorksSection ───────────────────────────────────────────────────────

function HowItWorksSection() {
  return (
    <section
      id="how-it-works"
      className="py-20 px-4 sm:px-6 lg:px-8"
      style={{
        background:
          "linear-gradient(180deg, transparent, oklch(0.958 0.011 82.64 / 0.4) 20%, oklch(0.958 0.011 82.64 / 0.4) 80%, transparent)",
      }}
    >
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-12 space-y-3">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-primary shell-kicker">
            How It Works
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight">
            Simple by design
          </h2>
          <p className="max-w-xl text-base leading-7 text-muted-foreground">
            Four steps from unboxing to full protection.
          </p>
        </div>

        {/* Steps */}
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {HOW_IT_WORKS_STEPS.map((step) => (
            <div
              key={step.step}
              className="shell-panel rounded-4xl border border-border/70 p-6 flex flex-col gap-4"
            >
              <span className="font-heading text-5xl font-bold text-primary/20 leading-none">
                {step.step}
              </span>
              <div className="space-y-2">
                <h3 className="font-heading text-lg leading-snug">{step.title}</h3>
                <p className="text-sm leading-6 text-muted-foreground">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <Button size="lg" variant="outline" asChild className="px-8 h-12 text-base">
            <a href="#kits">Request a Kit</a>
          </Button>
        </div>
      </div>
    </section>
  )
}

// ─── WhySafetyQRSection ───────────────────────────────────────────────────────

function WhySafetyQRSection() {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-12 space-y-3">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-primary shell-kicker">
            Why SafetyQR
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight">
            Safety that just works
          </h2>
          <p className="max-w-xl text-base leading-7 text-muted-foreground">
            Designed from the ground up for real Indian conditions — no complexity, no barriers.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-5">
          {BENEFITS.map((benefit) => (
            <BenefitCard key={benefit.title} benefit={benefit} />
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── BenefitCard ─────────────────────────────────────────────────────────────

function BenefitCard({
  benefit,
}: {
  benefit: { icon: LucideIcon; title: string; description: string }
}) {
  const Icon = benefit.icon
  return (
    <div className="shell-panel rounded-4xl border border-border/70 p-5 flex flex-col gap-3">
      <div className="rounded-2xl bg-primary/10 p-2.5 w-fit">
        <Icon className="size-5 text-primary" />
      </div>
      <strong className="font-heading text-lg leading-snug">{benefit.title}</strong>
      <p className="text-sm leading-6 text-muted-foreground">{benefit.description}</p>
    </div>
  )
}

// ─── LandingFooter ───────────────────────────────────────────────────────────

function LandingFooter() {
  return (
    <footer className="border-t border-border/50 py-14 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl grid gap-10 md:grid-cols-3">
        {/* Brand */}
        <div className="space-y-4">
          <a href="/" className="flex items-center gap-2.5">
            <img src="/feedbacknfc-logo.svg" alt="FeedbackNFC" className="h-7 w-auto" />
            <span className="font-heading font-semibold text-lg">SafetyQR</span>
          </a>
          <p className="text-sm leading-6 text-muted-foreground">
            QR sticker safety kits for every Indian — students, women, elderly, vehicles, and plantation sites.
          </p>
        </div>

        {/* Quick links */}
        <div className="space-y-4">
          <h3 className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground shell-kicker">
            Quick links
          </h3>
          <ul className="space-y-3">
            {[
              { label: "Activate a Kit", href: PUBLIC_ROUTE_LINKS.activate },
              { label: "Report an Incident", href: PUBLIC_ROUTE_LINKS.report },
              { label: "My SafetyQR", href: PUBLIC_ROUTE_LINKS.myDevice },
            ].map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Support */}
        <div className="space-y-4">
          <h3 className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground shell-kicker">
            Support
          </h3>
          <ul className="space-y-3">
            {[
              { label: "Help & Support", href: PUBLIC_ROUTE_LINKS.support },
              { label: "Privacy Policy", href: PUBLIC_ROUTE_LINKS.privacy },
              { label: "Terms of Service", href: PUBLIC_ROUTE_LINKS.terms },
              { label: "Tracked Support", href: "/grievance" },
            ].map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="mx-auto max-w-7xl mt-10 pt-8 border-t border-border/40">
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} FeedbackNFC. All rights reserved. SafetyQR is a product of FeedbackNFC.
        </p>
      </div>
    </footer>
  )
}

// ─── KitRequestSheet ─────────────────────────────────────────────────────────

function KitRequestSheet({
  open,
  kit,
  onClose,
}: {
  open: boolean
  kit: KitDescriptor | null
  onClose: () => void
}) {
  const readyKits = KITS.filter((k) => k.status === "ready")

  const initialForm = (): KitRequestFormValues => ({
    requesterName: "",
    phone: "",
    kitName: kit?.name ?? readyKits[0]?.name ?? "",
    area: "",
    quantity: "1",
    note: "",
  })

  const [form, setForm] = useState<KitRequestFormValues>(initialForm)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof KitRequestFormValues, string>>>({})

  // Reset when sheet opens or kit changes
  useEffect(() => {
    if (open) {
      setForm({ ...initialForm(), kitName: kit?.name ?? readyKits[0]?.name ?? "" })
      setSubmitted(false)
      setErrorMessage(null)
      setFieldErrors({})
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kit?.id])

  function updateField<K extends keyof KitRequestFormValues>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  function validate(): boolean {
    const errors: Partial<Record<keyof KitRequestFormValues, string>> = {}
    if (!form.requesterName.trim()) errors.requesterName = "Full name is required."
    if (!form.phone.trim()) {
      errors.phone = "Phone number is required."
    } else if (!isValidIndianPhone(form.phone)) {
      errors.phone = "Enter a valid Indian mobile number (e.g. 98765 43210)."
    }
    if (!form.kitName) errors.kitName = "Please select a kit."
    if (!form.area.trim()) errors.area = "Area or pincode is required."
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!validate()) return
    setLoading(true)
    setErrorMessage(null)
    try {
      const res = await fetch("/api/kit-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "request",
          requesterName: form.requesterName.trim(),
          phone: form.phone.trim(),
          kitName: form.kitName,
          area: form.area.trim(),
          quantity: form.quantity || "1",
          note: form.note.trim() || undefined,
        }),
      })
      const data = (await res.json()) as { success: boolean; error?: string }
      if (data.success) {
        setSubmitted(true)
      } else {
        setErrorMessage(data.error ?? "Something went wrong. Please try again.")
      }
    } catch {
      setErrorMessage("Network error. Please check your connection and try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <SheetContent side="right" className="sm:max-w-md flex flex-col overflow-y-auto gap-0">
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle className="text-xl">
            {kit ? `Request ${kit.name}` : "Request a Safety Kit"}
          </SheetTitle>
          <SheetDescription>
            Tell us where to send your kit. Our team will contact you within 2 business days.
          </SheetDescription>
        </SheetHeader>

        {submitted ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6 py-12 text-center">
            <div className="rounded-full bg-primary/10 p-4">
              <CheckCircleIcon className="size-10 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="font-heading text-xl">Request received!</h3>
              <p className="text-sm leading-6 text-muted-foreground">
                Thank you! Our team will contact you within 2 business days to arrange delivery.
              </p>
            </div>
            <Button variant="outline" onClick={onClose} className="mt-2">
              Close
            </Button>
          </div>
        ) : (
          <form
            className="flex flex-col gap-4 px-6 pb-4 flex-1"
            onSubmit={handleSubmit}
            noValidate
          >
            {/* Full name */}
            <label className="block">
              <span className={LABEL_CLASS}>Full Name <span className="text-destructive">*</span></span>
              <input
                className={INPUT_CLASS}
                type="text"
                placeholder="Your full name"
                value={form.requesterName}
                onChange={(e) => updateField("requesterName", e.target.value)}
                disabled={loading}
                aria-required="true"
              />
              {fieldErrors.requesterName && (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.requesterName}</p>
              )}
            </label>

            {/* Phone */}
            <label className="block">
              <span className={LABEL_CLASS}>Phone Number <span className="text-destructive">*</span></span>
              <input
                className={INPUT_CLASS}
                type="tel"
                placeholder="+91 98765 43210"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                disabled={loading}
                aria-required="true"
              />
              {fieldErrors.phone ? (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.phone}</p>
              ) : (
                <p className={HELP_CLASS}>Indian mobile number (+91 or 10 digits).</p>
              )}
            </label>

            {/* Kit selection */}
            <label className="block">
              <span className={LABEL_CLASS}>Kit <span className="text-destructive">*</span></span>
              <select
                className={`${INPUT_CLASS} appearance-none`}
                value={form.kitName}
                onChange={(e) => updateField("kitName", e.target.value)}
                disabled={loading}
                aria-required="true"
              >
                {readyKits.map((k) => (
                  <option key={k.id} value={k.name}>
                    {k.name}
                  </option>
                ))}
              </select>
              {fieldErrors.kitName && (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.kitName}</p>
              )}
            </label>

            {/* Area */}
            <label className="block">
              <span className={LABEL_CLASS}>Area / Pincode <span className="text-destructive">*</span></span>
              <input
                className={INPUT_CLASS}
                type="text"
                placeholder="City, area, or pincode"
                value={form.area}
                onChange={(e) => updateField("area", e.target.value)}
                disabled={loading}
                aria-required="true"
              />
              {fieldErrors.area && (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.area}</p>
              )}
            </label>

            {/* Quantity */}
            <label className="block">
              <span className={LABEL_CLASS}>Quantity <span className="text-muted-foreground text-xs font-normal">(optional)</span></span>
              <input
                className={INPUT_CLASS}
                type="number"
                min="1"
                placeholder="1"
                value={form.quantity}
                onChange={(e) => updateField("quantity", e.target.value)}
                disabled={loading}
              />
              <p className={HELP_CLASS}>Need multiple kits for an institution? Let us know.</p>
            </label>

            {/* Note */}
            <label className="block">
              <span className={LABEL_CLASS}>Note <span className="text-muted-foreground text-xs font-normal">(optional)</span></span>
              <textarea
                className={`${INPUT_CLASS} resize-none`}
                rows={3}
                placeholder="Any other details we should know..."
                value={form.note}
                onChange={(e) => updateField("note", e.target.value)}
                disabled={loading}
              />
            </label>

            {/* Error banner */}
            {errorMessage && (
              <div className="rounded-3xl border border-rose-300/80 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-900">
                {errorMessage}
              </div>
            )}

            <SheetFooter className="px-0 pb-2 mt-2">
              <Button type="submit" className="w-full h-11 text-base" disabled={loading}>
                {loading ? "Sending…" : "Submit Request"}
              </Button>
            </SheetFooter>
          </form>
        )}
      </SheetContent>
    </Sheet>
  )
}

// ─── NotifyMeSheet ───────────────────────────────────────────────────────────

function NotifyMeSheet({
  open,
  kit,
  onClose,
}: {
  open: boolean
  kit: KitDescriptor | null
  onClose: () => void
}) {
  const initialForm = (): NotifyMeFormValues => ({
    requesterName: "",
    contactValue: "",
    kitName: kit?.name ?? "",
  })

  const [form, setForm] = useState<NotifyMeFormValues>(initialForm)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof NotifyMeFormValues, string>>>({})

  useEffect(() => {
    if (open) {
      setForm({ ...initialForm(), kitName: kit?.name ?? "" })
      setSubmitted(false)
      setErrorMessage(null)
      setFieldErrors({})
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kit?.id])

  function updateField<K extends keyof NotifyMeFormValues>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  function validate(): boolean {
    const errors: Partial<Record<keyof NotifyMeFormValues, string>> = {}
    if (!form.requesterName.trim()) errors.requesterName = "Name is required."
    if (!form.contactValue.trim()) errors.contactValue = "Phone or email is required."
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!validate()) return
    setLoading(true)
    setErrorMessage(null)
    try {
      const res = await fetch("/api/kit-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "notify_me",
          requesterName: form.requesterName.trim(),
          contactValue: form.contactValue.trim(),
          kitName: form.kitName || kit?.name,
        }),
      })
      const data = (await res.json()) as { success: boolean; error?: string }
      if (data.success) {
        setSubmitted(true)
      } else {
        setErrorMessage(data.error ?? "Something went wrong. Please try again.")
      }
    } catch {
      setErrorMessage("Network error. Please check your connection and try again.")
    } finally {
      setLoading(false)
    }
  }

  const KitIcon = kit?.icon ?? QrCodeIcon

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <SheetContent side="right" className="sm:max-w-sm flex flex-col gap-0">
        <SheetHeader className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="rounded-2xl bg-muted p-2">
              <KitIcon className="size-4 text-muted-foreground" />
            </div>
            {kit && (
              <Badge variant="secondary" className="text-xs">
                Coming Soon
              </Badge>
            )}
          </div>
          <SheetTitle className="text-xl">
            {kit ? `Notify me: ${kit.name}` : "Get notified"}
          </SheetTitle>
          <SheetDescription>
            We'll let you know the moment this kit is ready to order.
          </SheetDescription>
        </SheetHeader>

        {submitted ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6 py-12 text-center">
            <div className="rounded-full bg-primary/10 p-4">
              <BellIcon className="size-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="font-heading text-lg">You're on the list!</h3>
              <p className="text-sm leading-6 text-muted-foreground">
                We'll notify you as soon as {kit?.name ?? "this kit"} launches.
              </p>
            </div>
            <Button variant="outline" onClick={onClose} className="mt-2">
              Close
            </Button>
          </div>
        ) : (
          <form
            className="flex flex-col gap-4 px-6 pb-4 flex-1"
            onSubmit={handleSubmit}
            noValidate
          >
            {/* Kit display (read-only) */}
            {kit && (
              <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3">
                <p className="text-xs text-muted-foreground">Kit of interest</p>
                <p className="text-sm font-medium mt-0.5">{kit.name}</p>
              </div>
            )}

            {/* Name */}
            <label className="block">
              <span className={LABEL_CLASS}>Your Name <span className="text-destructive">*</span></span>
              <input
                className={INPUT_CLASS}
                type="text"
                placeholder="Your name"
                value={form.requesterName}
                onChange={(e) => updateField("requesterName", e.target.value)}
                disabled={loading}
                aria-required="true"
              />
              {fieldErrors.requesterName && (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.requesterName}</p>
              )}
            </label>

            {/* Contact */}
            <label className="block">
              <span className={LABEL_CLASS}>Phone or Email <span className="text-destructive">*</span></span>
              <input
                className={INPUT_CLASS}
                type="text"
                placeholder="Phone or email address"
                value={form.contactValue}
                onChange={(e) => updateField("contactValue", e.target.value)}
                disabled={loading}
                aria-required="true"
              />
              {fieldErrors.contactValue ? (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.contactValue}</p>
              ) : (
                <p className={HELP_CLASS}>We'll use this to notify you at launch.</p>
              )}
            </label>

            {/* Error banner */}
            {errorMessage && (
              <div className="rounded-3xl border border-rose-300/80 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-900">
                {errorMessage}
              </div>
            )}

            <SheetFooter className="px-0 pb-2 mt-auto">
              <Button type="submit" className="w-full h-11" disabled={loading}>
                {loading ? "Sending…" : "Notify me at launch"}
              </Button>
            </SheetFooter>
          </form>
        )}
      </SheetContent>
    </Sheet>
  )
}
